/**
 * Public share link view — no login required.
 * Accessed via /share/:token
 */
import { useState, useEffect } from 'react'
import { StatusReportBody } from '../components/project/WeeklyStatusCard'
import { useParams } from 'react-router-dom'
import { Lock, CheckCircle, AlertTriangle, Calendar, DollarSign } from 'lucide-react'
import { shareLinksService, type PublicProjectView } from '../services/shareLinks'
import { DateDisplay } from '../components/ui/DateDisplay'
import { HealthIndicator } from '../components/ui/HealthIndicator'
import { useTranslation } from '../i18n/I18nContext'
import type { ApiErrorLike } from '../utils/apiError'

type ViewState = 'loading' | 'password' | 'loaded' | 'error' | 'expired' | 'revoked'

export default function ShareLinkView() {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<ViewState>('loading')
  const [data, setData] = useState<PublicProjectView | null>(null)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => { if (token) loadView() }, [token])

  const loadView = async (pw?: string) => {
    setState('loading')
    try {
      const result = await shareLinksService.getPublicView(token!, pw)
      setData(result)
      setState('loaded')
    } catch (errRaw) {
      const err = errRaw as ApiErrorLike
      const status = err?.response?.status
      if (status === 401) { setState('password'); return }
      if (status === 403) { setPasswordError('Incorrect password'); setState('password'); return }
      if (status === 410) { setState('expired'); return }
      if (status === 404) { setState('revoked'); return }
      setErrorMsg(err?.response?.data?.detail || 'Failed to load')
      setState('error')
    }
  }

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) return
    setPasswordError('')
    loadView(password)
  }

  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#486581] mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Loading…</p>
        </div>
      </div>
    )
  }

  if (state === 'password') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 w-full max-w-sm shadow-lg text-center">
          <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-5 h-5 text-[#486581]" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Password required</h1>
          <p className="text-sm text-gray-500 mb-6">This link is password-protected.</p>
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <div>
              <label htmlFor="share-password" className="sr-only">Password</label>
              <input
                id="share-password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
                autoComplete="current-password"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#486581] min-h-[44px]"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'pw-error' : undefined}
              />
              {passwordError && (
                <p id="pw-error" role="alert" className="text-xs text-red-600 mt-1 text-left">{passwordError}</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full py-2.5 bg-[#486581] text-white text-sm font-medium rounded-lg hover:bg-[#3d5570] transition-colors min-h-[44px]"
            >
              View project
            </button>
          </form>
        </div>
      </div>
    )
  }

  if (state === 'expired') {
    return <ErrorPage icon="⏰" title="Link expired" message="This share link is no longer valid." />
  }
  if (state === 'revoked') {
    return <ErrorPage icon="🔒" title="Link revoked" message="This share link has been revoked by the project owner." />
  }
  if (state === 'error') {
    return <ErrorPage icon="⚠️" title="Something went wrong" message={errorMsg || 'Unable to load this link.'} />
  }
  if (!data) return null

  const cfg = data.visibility_config

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{data.project_name}</h1>
            {data.org_name && <p className="text-xs text-gray-500 mt-0.5">{data.org_name}</p>}
          </div>
          {cfg.show_status && data.project_status && (
            <HealthIndicator health={data.project_status} />
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-6">
        {/* Milestones */}
        {cfg.show_milestones && data.milestones && (
          <section aria-labelledby="milestones-heading">
            <h2 id="milestones-heading" className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#486581]" aria-hidden="true" />
              Milestones
            </h2>
            {/* Surface the display timezone so external viewers read deadlines
                unambiguously across time zones (store-UTC / render-local). */}
            <p className="text-xs text-gray-400 mb-3">
              {t('share.timezoneNote', { tz: data.timezone || 'UTC' })}
            </p>
            {data.milestones.length === 0 ? (
              <p className="text-sm text-gray-400">No milestones</p>
            ) : (
              <ul className="space-y-2">
                {data.milestones.map((m, i) => (
                  <li key={i} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle
                        className={`w-4 h-4 flex-shrink-0 ${m.status === 'completed' ? 'text-green-500' : 'text-gray-300'}`}
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-900">{m.name}</span>
                    </div>
                    {m.due_date && (
                      <DateDisplay
                        date={m.due_date}
                        timezone={data.timezone || 'UTC'}
                        className="text-xs text-gray-400"
                      />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* Weekly status report (2026-09-28) */}
        {cfg.show_status_report && data.status_report && (
          <section aria-labelledby="report-heading" className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <h2 id="report-heading" className="text-sm font-semibold text-gray-700">Weekly status report</h2>
            <StatusReportBody report={data.status_report} />
          </section>
        )}

        {/* Budget summary */}
        {cfg.show_budget_summary && data.budget_summary && (
          <section aria-labelledby="budget-heading">
            <h2 id="budget-heading" className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#486581]" aria-hidden="true" />
              Budget summary
            </h2>
            <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
              <p className="text-sm text-gray-600">
                Total budget: <span className="font-semibold text-gray-900">
                  {parseFloat(data.budget_summary.total_budget).toLocaleString('en-US', { style: 'currency', currency: data.budget_summary.currency || 'USD' })}
                </span>
              </p>
            </div>
          </section>
        )}

        {/* Flagged risks */}
        {cfg.show_flagged_risks && data.flagged_risks && data.flagged_risks.length > 0 && (
          <section aria-labelledby="risks-heading">
            <h2 id="risks-heading" className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-500" aria-hidden="true" />
              Flagged risks
            </h2>
            <ul className="space-y-2">
              {data.flagged_risks.map((r, i) => (
                <li key={i} className="bg-white border border-orange-100 rounded-xl px-4 py-3 text-sm text-gray-700">
                  {r.description || r.title || `Risk ${i + 1}`}
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="text-center py-6 text-xs text-gray-400">
        Shared read-only view · Powered by Karyva
      </footer>
    </div>
  )
}

function ErrorPage({ icon, title, message }: { icon: string; title: string; message: string }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="text-center max-w-sm">
        <div className="text-4xl mb-4" aria-hidden="true">{icon}</div>
        <h1 className="text-lg font-semibold text-gray-900 mb-2">{title}</h1>
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    </div>
  )
}
